import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosHeaders, AxiosResponse, RawAxiosResponseHeaders } from 'axios';
import { ArrowDown, ArrowUp, Check, ChevronDown, CircleHelp, Download, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LoadingAnimation } from 'components/ui/loading-animation';
import { PaginationControls } from 'components/ui/pagination';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { useToast } from 'components/ui/use-toast';
import { useEventTracking } from 'hooks/useEventTracking';
import { adminAnalytics } from 'services/api';
import { toTitleCase } from 'utils/textUtils';
import type {
  AdminAnalyticsDashboardData,
  AdminAnalyticsDeviceGroupRow,
  AdminAnalyticsDistributionRow,
  AdminAnalyticsFilterOption,
  AdminAnalyticsFiltersResponse,
  AdminAnalyticsFlowRow,
  AdminAnalyticsRouteRow,
  AdminAnalyticsTrackedCountRow,
  AdminAnalyticsUserRow,
  AdminAnalyticsUsersResponse,
} from 'types';
import {
  ADMIN_ANALYTICS_DEFAULT_USER_SORT,
  buildAdminAnalyticsSearchParams,
  getAdminAnalyticsPresetRange,
  parseAdminAnalyticsSearchParams,
  type AdminAnalyticsPresetValue,
  type AdminAnalyticsTabValue,
} from './adminAnalyticsQueryParams';

import './AdminAnalytics.css';
import PerformanceMetricsCard from './PerformanceMetricsCard';

type TabValue = AdminAnalyticsTabValue;
type PresetValue = AdminAnalyticsPresetValue;
type DropdownValue = 'organizations' | 'cohorts' | 'flows' | null;
type UserSortField =
  | 'user'
  | 'organization'
  | 'cohort'
  | 'primary_device'
  | 'browsers'
  | 'os'
  | 'page_views'
  | 'started_runs'
  | 'completed_runs'
  | 'completion_rate'
  | 'last_route'
  | 'last_active';
type UserSortDirection = 'asc' | 'desc';
type FlowSortField =
  | 'flow_name'
  | 'started_runs'
  | 'completed_runs'
  | 'run_completion_rate'
  | 'voice_only_completed_runs'
  | 'standard_only_completed_runs'
  | 'mixed_mode_completed_runs'
  | 'started_users'
  | 'completed_users'
  | 'user_completion_rate'
  | 'median_completion_minutes'
  | 'entry_events'
  | 'avg_entries_per_run';
type FlowSortDirection = 'asc' | 'desc';

const TAB_ORDER: Array<{ id: TabValue; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'devices', label: 'Devices' },
  { id: 'screens', label: 'Screens' },
  { id: 'flows', label: 'Modules' },
  { id: 'users', label: 'Users' },
  { id: 'quality', label: 'Quality' },
  { id: 'performance', label: 'App Performance' },
];

const USER_SORT_COLUMNS: Array<{
  field: UserSortField;
  label: string;
  align?: 'left' | 'right';
  defaultDirection: UserSortDirection;
}> = [
  { field: 'user', label: 'User', defaultDirection: 'asc' },
  { field: 'organization', label: 'Organization', defaultDirection: 'asc' },
  { field: 'cohort', label: 'Cohort', defaultDirection: 'asc' },
  { field: 'primary_device', label: 'Primary device', defaultDirection: 'asc' },
  { field: 'browsers', label: 'Browsers', defaultDirection: 'asc' },
  { field: 'os', label: 'OS', defaultDirection: 'asc' },
  { field: 'page_views', label: 'Page views', align: 'right', defaultDirection: 'desc' },
  { field: 'started_runs', label: 'Started modules', align: 'right', defaultDirection: 'desc' },
  { field: 'completed_runs', label: 'Completed modules', align: 'right', defaultDirection: 'desc' },
  { field: 'completion_rate', label: 'Completion rate', align: 'right', defaultDirection: 'desc' },
  { field: 'last_route', label: 'Last route', defaultDirection: 'asc' },
  { field: 'last_active', label: 'Last active', align: 'right', defaultDirection: 'desc' },
];

const FLOW_SORT_COLUMNS: Array<{
  field: FlowSortField;
  label: string;
  align?: 'left' | 'right';
  defaultDirection: FlowSortDirection;
}> = [
  { field: 'flow_name', label: 'Module', defaultDirection: 'asc' },
  { field: 'started_runs', label: 'Started modules', align: 'right', defaultDirection: 'desc' },
  { field: 'completed_runs', label: 'Completed modules', align: 'right', defaultDirection: 'desc' },
  { field: 'run_completion_rate', label: 'Module completion', align: 'right', defaultDirection: 'desc' },
  { field: 'voice_only_completed_runs', label: 'Voice-only', align: 'right', defaultDirection: 'desc' },
  { field: 'standard_only_completed_runs', label: 'Standard-only', align: 'right', defaultDirection: 'desc' },
  { field: 'mixed_mode_completed_runs', label: 'Mixed', align: 'right', defaultDirection: 'desc' },
  { field: 'started_users', label: 'Started users', align: 'right', defaultDirection: 'desc' },
  { field: 'completed_users', label: 'Completed users', align: 'right', defaultDirection: 'desc' },
  { field: 'user_completion_rate', label: 'User completion', align: 'right', defaultDirection: 'desc' },
  { field: 'median_completion_minutes', label: 'Median completion minutes', align: 'right', defaultDirection: 'desc' },
  { field: 'entry_events', label: 'Entry events', align: 'right', defaultDirection: 'desc' },
  { field: 'avg_entries_per_run', label: 'Avg entries / module', align: 'right', defaultDirection: 'desc' },
];

const CHART_COLORS = ['#353947', '#fd4319', '#7f89a7', '#a9afc5', '#6a09dd'];
const CHART_TRACK_COLOR = '#f2f3f6';
const CHART_TEXT_MUTED = '#787a84';

const ANALYTICS_TOOLTIP_COPY = {
  overview_active_users: 'Distinct users with tracked activity in the selected date range.',
  overview_returning_users:
    'Users with selected-range activity who were not first seen during the same selected range.',
  overview_started_modules: 'Module attempts that started in the selected date range.',
  overview_completed_modules:
    'Completed module attempts among the runs that started in the selected date range.',
  overview_module_completion_rate:
    'Completed modules divided by started modules for attempts that began in the selected date range.',
  overview_voice_only_completions:
    'Completed module attempts where all saved answers were recorded in voice mode.',
  overview_standard_only_completions:
    'Completed module attempts where all saved answers were recorded in standard mode.',
  overview_mixed_mode_completions:
    'Completed module attempts that used both standard and voice answers.',
  flows_started_modules: 'Module attempts that started in the selected date range.',
  flows_completed_modules:
    'Completed module attempts among the runs that started in the selected date range.',
  flows_module_completion:
    'Completed modules divided by started modules for this module within the selected range.',
  flows_voice_only:
    'Completed module attempts for this module where all saved answers were voice.',
  flows_standard_only:
    'Completed module attempts for this module where all saved answers were standard.',
  flows_mixed:
    'Completed module attempts for this module that used both standard and voice answers.',
  flows_started_users: 'Distinct users who started this module in the selected range.',
  flows_completed_users: 'Distinct users who completed this module in the selected range.',
  flows_user_completion:
    'Completed users divided by started users for this module in the selected range.',
  flows_median_completion_minutes:
    'Median minutes between module start and completion for completed attempts in the selected range.',
  flows_entry_events: 'Tracked entry events for this module in the selected range.',
  flows_avg_entries_per_module:
    'Entry events divided by started module attempts for this module in the selected range.',
  flows_answers_by_mode:
    'Counts reflect the latest canonical completed response per question within modules started in the selected period.',
  devices_device_class_legacy:
    'Based on each user’s primary device class, not every device class ever seen for that user.',
  screens_conversion:
    'Downstream tracked actions divided by route views for the selected range.',
  screens_low_downstream_action:
    'Downstream action means tracked next-step or button-click behavior after the screen was viewed.',
  quality_support_cta_clicks:
    'Explicit support-seeking clicks tracked through the support CTA analytics action.',
  quality_toolkit_note_activity:
    'Sum of toolkit note click, create, and close events in the selected range.',
  users_primary_device_legacy:
    'This user’s primary device class, not every device class observed for the user.',
  users_completion_rate:
    'Completed modules divided by started modules for this user in the selected range.',
  devices_device_class:
    "Based on each user's primary device class, not every device class ever seen for that user.",
  users_primary_device:
    "This user's primary device class, not every device class observed for the user.",
} as const;

type AnalyticsTooltipKey = keyof typeof ANALYTICS_TOOLTIP_COPY;

const formatDistributionLabel = (label: string) => toTitleCase(label.replace(/[_-]+/g, ' ').trim());

const getFilenameFromContentDisposition = (header?: string | null) => {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  const unquotedMatch = header.match(/filename=([^;]+)/i);
  return unquotedMatch?.[1]?.trim() ?? null;
};

const getContentDispositionHeader = (headers?: RawAxiosResponseHeaders | AxiosHeaders) => {
  if (!headers) {
    return null;
  }
  if (typeof (headers as AxiosHeaders).get === 'function') {
    const headerValue = (headers as AxiosHeaders).get('content-disposition');
    return typeof headerValue === 'string' ? headerValue : null;
  }
  const headerValue = (headers as RawAxiosResponseHeaders)['content-disposition'];
  if (Array.isArray(headerValue)) {
    return headerValue.join('; ');
  }
  return typeof headerValue === 'string' ? headerValue : null;
};

const downloadBlobResponse = (response: AxiosResponse<Blob>, fallbackFilename: string) => {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/csv' });
  const filename = getFilenameFromContentDisposition(getContentDispositionHeader(response.headers)) || fallbackFilename;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const formatNumber = (value: number) => value.toLocaleString();
const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
const formatTitleCase = (value: string | null | undefined) =>
  (value ?? '')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const formatDateTime = (value: string | null) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const parseUserSort = (sort: string): { field: UserSortField; direction: UserSortDirection } => {
  const aliasMap: Record<string, string> = {
    email_asc: 'user_asc',
    organization_asc: 'organization_asc',
  };
  const normalizedSort = aliasMap[sort] ?? sort;
  const lastUnderscoreIndex = normalizedSort.lastIndexOf('_');
  if (lastUnderscoreIndex === -1) {
    return { field: 'last_active', direction: 'desc' };
  }
  const field = normalizedSort.slice(0, lastUnderscoreIndex) as UserSortField;
  const direction = normalizedSort.slice(lastUnderscoreIndex + 1) === 'asc' ? 'asc' : 'desc';
  return { field, direction };
};

const getSelectedOptionNames = (options: AdminAnalyticsFilterOption[], value: string[]) =>
  options.filter((option) => value.includes(option.id)).map((option) => option.name);

const getFilterSummary = (options: AdminAnalyticsFilterOption[], value: string[]) => {
  if (!value.length) {
    return 'All';
  }
  const names = getSelectedOptionNames(options, value);
  if (names.length <= 2) {
    return names.join(', ');
  }
  return `${names.length} selected`;
};

const getPrimaryMetricClassName = (accent?: boolean) =>
  `wfaa-kpi${accent ? ' wfaa-kpi-accent' : ''}`;

const FLOW_HEADER_TOOLTIPS: Partial<Record<FlowSortField, AnalyticsTooltipKey>> = {
  started_runs: 'flows_started_modules',
  completed_runs: 'flows_completed_modules',
  run_completion_rate: 'flows_module_completion',
  voice_only_completed_runs: 'flows_voice_only',
  standard_only_completed_runs: 'flows_standard_only',
  mixed_mode_completed_runs: 'flows_mixed',
  started_users: 'flows_started_users',
  completed_users: 'flows_completed_users',
  user_completion_rate: 'flows_user_completion',
  median_completion_minutes: 'flows_median_completion_minutes',
  entry_events: 'flows_entry_events',
  avg_entries_per_run: 'flows_avg_entries_per_module',
};

const USER_HEADER_TOOLTIPS: Partial<Record<UserSortField, AnalyticsTooltipKey>> = {
  primary_device: 'users_primary_device',
  completion_rate: 'users_completion_rate',
};

const AnalyticsInfoTooltip: React.FC<{
  tooltipKey: AnalyticsTooltipKey;
  label: string;
  testId?: string;
}> = ({ tooltipKey, label, testId }) => (
  <UiTooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="wfaa-info-trigger"
        aria-label={`More info about ${label}`}
        data-testid={testId}
      >
        <CircleHelp aria-hidden="true" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="wfaa-tooltip-content">{ANALYTICS_TOOLTIP_COPY[tooltipKey]}</TooltipContent>
  </UiTooltip>
);

const AnalyticsLabelWithInfo: React.FC<{
  label: string;
  tooltipKey?: AnalyticsTooltipKey;
  className?: string;
  testId?: string;
}> = ({ label, tooltipKey, className, testId }) => (
  <span className={`wfaa-label-with-info${className ? ` ${className}` : ''}`}>
    <span>{label}</span>
    {tooltipKey ? <AnalyticsInfoTooltip tooltipKey={tooltipKey} label={label} testId={testId} /> : null}
  </span>
);

const SectionHeader: React.FC<{
  title: string;
  description: string;
  actions?: React.ReactNode;
}> = ({ title, description, actions }) => (
  <div className="wfaa-section-head">
    <div>
      <h2 className="wfaa-section-title">{title}</h2>
      <p className="wfaa-section-sub">{description}</p>
    </div>
    {actions ? <div className="wfaa-section-actions">{actions}</div> : null}
  </div>
);

const ShellCard: React.FC<{
  title?: React.ReactNode;
  eyebrow?: string;
  action?: React.ReactNode;
  flush?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ title, eyebrow, action, flush = false, className = '', children }) => (
  <section className={`wfaa-card${flush ? ' wfaa-card-flush' : ''}${className ? ` ${className}` : ''}`}>
    {title || eyebrow || action ? (
      <header className="wfaa-card-head">
        <div>
          {eyebrow ? <span className="wfaa-eyebrow">{eyebrow}</span> : null}
          {title ? <h3 className="wfaa-card-title">{title}</h3> : null}
        </div>
        {action ? <div>{action}</div> : null}
      </header>
    ) : null}
    {children}
  </section>
);

const MetricCard: React.FC<{
  label: React.ReactNode;
  value: string | number;
  accent?: boolean;
}> = ({ label, value, accent = false }) => (
  <article className={getPrimaryMetricClassName(accent)}>
    <div className="wfaa-kpi-head">
      <span className="wfaa-kpi-label">{label}</span>
    </div>
    <div className="wfaa-kpi-value">{value}</div>
  </article>
);

const DeviceClassChartCard: React.FC<{
  title: React.ReactNode;
  rows: AdminAnalyticsDistributionRow[];
}> = ({ title, rows }) => {
  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <ShellCard title={title} className="wfaa-device-class-card">
      <div className="wfaa-donut-wrap">
        <div className="wfaa-device-donut-column">
          <div className="wfaa-donut-chart">
            <div className="wfaa-donut-center-copy" aria-hidden="true">
              <span className="wfaa-donut-center-value">{formatNumber(total)}</span>
              <span className="wfaa-donut-center-label">Users</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={44}
                  outerRadius={62}
                  paddingAngle={2}
                  stroke="#ffffff"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {rows.map((row, index) => (
                    <Cell key={row.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <ul className="wfaa-legend-list">
          {rows.map((row, index) => (
            <li key={row.label}>
              <span
                className="wfaa-legend-dot"
                style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
              />
              <span className="wfaa-legend-name">{formatDistributionLabel(row.label)}</span>
              <span className="wfaa-legend-value">{formatNumber(row.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    </ShellCard>
  );
};

const HorizontalDistributionCard: React.FC<{
  title: React.ReactNode;
  rows: AdminAnalyticsDistributionRow[];
}> = ({ title, rows }) => {
  const chartWidth = 252;
  const pad = { left: 86, right: 10, top: 8, bottom: 8 };
  const rowHeight = 34;
  const barHeight = 20;
  const chartHeight = pad.top + pad.bottom + rows.length * rowHeight;
  const innerWidth = chartWidth - pad.left - pad.right;
  const maxValue = Math.max(...rows.map((row) => row.value), 1);

  return (
    <ShellCard title={title} className="wfaa-distribution-bar-card">
      <div className="wfaa-distribution-bar-chart">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          width="100%"
          height={chartHeight}
          aria-hidden="true"
          className="wfaa-distribution-svg"
        >
          {rows.map((row, index) => {
            const barWidth = (row.value / maxValue) * innerWidth;
            const y = pad.top + index * rowHeight + (rowHeight - barHeight) / 2;

            return (
              <g key={row.label}>
                <text
                  x={pad.left - 8}
                  y={y + barHeight / 2 + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill={CHART_TEXT_MUTED}
                >
                  {formatDistributionLabel(row.label)}
                </text>
                <rect x={pad.left} y={y} width={innerWidth} height={barHeight} fill={CHART_TRACK_COLOR} rx="3" />
                <rect x={pad.left} y={y} width={barWidth} height={barHeight} fill={CHART_COLORS[0]} rx="3" />
                <text
                  x={pad.left + barWidth + 6}
                  y={y + barHeight / 2 + 4}
                  fontSize="11"
                  fill={CHART_TEXT_MUTED}
                  className="wfaa-chart-number"
                >
                  {formatNumber(row.value)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </ShellCard>
  );
};

const DeviceSplitBar: React.FC<{ row: AdminAnalyticsDeviceGroupRow }> = ({ row }) => {
  const total = row.total || 1;
  const segments = [
    { key: 'mobile', value: row.mobile, className: 'wfaa-split-mobile' },
    { key: 'desktop', value: row.desktop, className: 'wfaa-split-desktop' },
    { key: 'tablet', value: row.tablet, className: 'wfaa-split-tablet' },
    { key: 'unknown', value: row.unknown, className: 'wfaa-split-unknown' },
  ];

  return (
    <div className="wfaa-split-bar" aria-hidden="true">
      {segments.map((segment) => (
        <span
          key={segment.key}
          className={segment.className}
          style={{ width: `${(segment.value / total) * 100}%` }}
        />
      ))}
    </div>
  );
};

const DeviceGroupTable: React.FC<{
  title: string;
  rows: AdminAnalyticsDeviceGroupRow[];
  labelKey: 'organization_name' | 'cohort_name';
}> = ({ title, rows, labelKey }) => (
  <ShellCard title={title} flush>
    <div className="wfaa-table-wrap">
      <table className="wfaa-table wfaa-table-compact">
        <thead>
          <tr>
            <th>{labelKey === 'organization_name' ? 'Organization' : 'Cohort'}</th>
            <th className="wfaa-num">Total</th>
            <th className="wfaa-split-col">Split</th>
            <th className="wfaa-num">Mobile</th>
            <th className="wfaa-num">Desktop</th>
            <th className="wfaa-num">Tablet</th>
            <th className="wfaa-num">Unknown</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row[labelKey] ?? 'unknown'}-${row.total}`}>
              <td className="wfaa-strong">{row[labelKey] ?? 'Unknown'}</td>
              <td className="wfaa-num">{formatNumber(row.total)}</td>
              <td className="wfaa-split-col">
                <DeviceSplitBar row={row} />
              </td>
              <td className="wfaa-num">{formatNumber(row.mobile)}</td>
              <td className="wfaa-num">{formatNumber(row.desktop)}</td>
              <td className="wfaa-num">{formatNumber(row.tablet)}</td>
              <td className="wfaa-num">{formatNumber(row.unknown)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ShellCard>
);

const FilterDropdown: React.FC<{
  label: string;
  options: AdminAnalyticsFilterOption[];
  value: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (nextValue: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  testId?: string;
}> = ({
  label,
  options,
  value,
  open,
  onOpenChange,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search',
  testId,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!open) {
      if (searchValue) {
        setSearchValue('');
      }
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onOpenChange, open, searchValue]);

  const summary = getFilterSummary(options, value);
  const filteredOptions = searchable
    ? options.filter((option) => option.name.toLowerCase().includes(searchValue.trim().toLowerCase()))
    : options;

  return (
    <div className="wfaa-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="wfaa-dd-button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        data-testid={testId}
      >
        <span className="wfaa-dd-label">{label}</span>
        <span className="wfaa-dd-value" title={summary}>
          {summary}
        </span>
        <ChevronDown className="wfaa-dd-icon" />
      </button>
      {open ? (
        <div className="wfaa-dd-menu" role="menu">
          {searchable ? (
            <div className="wfaa-dd-search">
              <Search className="h-4 w-4" />
              <input
                type="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.currentTarget.value)}
                placeholder={searchPlaceholder}
                aria-label={`${label} search`}
              />
            </div>
          ) : null}
          <button
            type="button"
            className={`wfaa-dd-item${value.length === 0 ? ' is-active' : ''}`}
            onClick={() => onChange([])}
          >
            <span>All</span>
            {value.length === 0 ? <Check className="h-4 w-4" /> : null}
          </button>
          {filteredOptions.map((option) => {
            const isSelected = value.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={`wfaa-dd-item${isSelected ? ' is-active' : ''}`}
                onClick={() => {
                  if (isSelected) {
                    onChange(value.filter((current) => current !== option.id));
                    return;
                  }
                  onChange([...value, option.id]);
                }}
              >
                <span>{option.name}</span>
                {isSelected ? <Check className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const UserSampleTable: React.FC<{ rows: AdminAnalyticsUserRow[] }> = ({ rows }) => (
  <ShellCard eyebrow="Sample" title="Per-user device usage" flush>
    <div className="wfaa-table-wrap">
      <table className="wfaa-table wfaa-table-compact">
        <thead>
          <tr>
            <th>User</th>
            <th>Org</th>
            <th>Primary</th>
            <th>Browsers</th>
            <th>OS</th>
            <th>Last active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((user) => (
            <tr key={user.user_id}>
              <td className="wfaa-strong">{user.display_name}</td>
              <td>{user.organization_name ?? '-'}</td>
              <td>
                <span className="wfaa-chip">{user.primary_device_class}</span>
              </td>
              <td className="wfaa-chip-cell">
                {user.browsers_seen.length
                  ? user.browsers_seen.map((browser) => (
                      <span key={browser} className="wfaa-chip wfaa-chip-subtle">
                        {browser}
                      </span>
                    ))
                  : '-'}
              </td>
              <td className="wfaa-chip-cell">
                {user.oses_seen.length
                  ? user.oses_seen.map((os) => (
                      <span key={os} className="wfaa-chip wfaa-chip-subtle">
                        {os}
                      </span>
                    ))
                  : '-'}
              </td>
              <td className="wfaa-muted">{formatDateTime(user.last_active_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ShellCard>
);

const TopRoutesTable: React.FC<{ rows: AdminAnalyticsRouteRow[] }> = ({ rows }) => (
  <ShellCard title="Top routes by page views" eyebrow="Volume" flush className="wfaa-screen-routes-card">
    <div className="wfaa-table-wrap">
      <table className="wfaa-table wfaa-table-compact">
        <thead>
          <tr>
            <th>Route</th>
            <th className="wfaa-num">Views</th>
            <th className="wfaa-num">Unique users</th>
            <th className="wfaa-num">
              <AnalyticsLabelWithInfo label="Conv. to action" tooltipKey="screens_conversion" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.screen_name}>
              <td className="wfaa-mono wfaa-screen-route-cell">{row.screen_name}</td>
              <td className="wfaa-num">{formatNumber(row.views)}</td>
              <td className="wfaa-num">{formatNumber(row.unique_users)}</td>
              <td className="wfaa-num">{formatPercent(row.conversion_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ShellCard>
);

const TopButtonActionsCard: React.FC<{ rows: { action: string; value: number }[] }> = ({ rows }) => {
  const chartWidth = 620;
  const rowCount = Math.max(rows.length, 1);
  const rowHeight = 44;
  const rowGap = 2;
  const chartHeight = rowCount * rowHeight;
  const viewBoxBleed = { left: 58, right: 96 };
  const pad = { left: 178, right: 26 };
  const innerWidth = chartWidth - pad.left - pad.right;
  const maxValue = Math.max(...rows.map((row) => row.value), 1);

  return (
    <ShellCard
      title="Top button click actions"
      eyebrow="Event names"
      className="wfaa-screen-top-card wfaa-screen-actions-card"
    >
      <div className="wfaa-screen-actions-chart">
        <svg
          viewBox={`-${viewBoxBleed.left} 0 ${chartWidth + viewBoxBleed.left + viewBoxBleed.right} ${chartHeight}`}
          width="100%"
          height={chartHeight}
          aria-hidden="true"
        >
          {rows.map((row, index) => {
            const y = index * rowHeight + rowGap / 2;
            const barHeight = rowHeight - rowGap;
            const barWidth = (row.value / maxValue) * innerWidth;

            return (
              <g key={row.action}>
                <text
                  x={pad.left - 10}
                  y={y + barHeight / 2}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="14"
                  fill={CHART_TEXT_MUTED}
                >
                  {row.action}
                </text>
                <rect x={pad.left} y={y} width={innerWidth} height={barHeight} fill={CHART_TRACK_COLOR} rx="3" />
                <rect x={pad.left} y={y} width={barWidth} height={barHeight} fill="#353947" rx="3" />
                <text
                  x={pad.left + barWidth + 8}
                  y={y + barHeight / 2}
                  dominantBaseline="middle"
                  fontSize="13"
                  fill={CHART_TEXT_MUTED}
                  className="wfaa-chart-number"
                >
                  {formatNumber(row.value)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </ShellCard>
  );
};

const getScreenRecommendation = (row: AdminAnalyticsRouteRow): string => {
  if (row.conversion_rate < 0.1) {
    return 'Audit primary CTA';
  }
  if (row.conversion_rate < 0.2) {
    return 'Simplify next step';
  }
  return 'Monitor';
};

const LowConversionTable: React.FC<{ rows: AdminAnalyticsRouteRow[] }> = ({ rows }) => (
  <ShellCard
    title={<AnalyticsLabelWithInfo label="High views, low downstream action" tooltipKey="screens_low_downstream_action" />}
    eyebrow="Attention flags"
    flush
  >
    <p className="wfaa-note-copy wfaa-screen-action-note">
      Screens that get traffic but where users don&apos;t follow through with a tracked button click.
    </p>
    <div className="wfaa-table-wrap">
      <table className="wfaa-table wfaa-table-compact">
        <thead>
          <tr>
            <th>Route</th>
            <th className="wfaa-num">Views</th>
            <th className="wfaa-num">
              <AnalyticsLabelWithInfo label="Conv." tooltipKey="screens_conversion" />
            </th>
            <th>
              <AnalyticsLabelWithInfo label="Conversion" tooltipKey="screens_conversion" />
            </th>
            <th>Recommendation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`low-${row.screen_name}`}>
              <td className="wfaa-mono wfaa-screen-route-cell">{row.screen_name}</td>
              <td className="wfaa-num">{formatNumber(row.views)}</td>
              <td className="wfaa-num">{formatPercent(row.conversion_rate)}</td>
              <td>
                <div className="wfaa-prog-track">
                  <div
                    className="wfaa-prog-fill wfaa-prog-fill-attention"
                    style={{ width: `${Math.round(row.conversion_rate * 100)}%` }}
                  />
                </div>
              </td>
              <td className="wfaa-muted">{getScreenRecommendation(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ShellCard>
);

const FlowPerformanceTable: React.FC<{ rows: AdminAnalyticsFlowRow[] }> = ({ rows }) => {
  const [activeSort, setActiveSort] = useState<{ field: FlowSortField; direction: FlowSortDirection }>({
    field: 'started_runs',
    direction: 'desc',
  });

  const sortedRows = useMemo(() => {
    const directionMultiplier = activeSort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
      const leftValue = left[activeSort.field];
      const rightValue = right[activeSort.field];

      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        return leftValue.localeCompare(rightValue) * directionMultiplier;
      }

      const normalizedLeft =
        typeof leftValue === 'number' ? leftValue : leftValue == null ? -Infinity : Number(leftValue);
      const normalizedRight =
        typeof rightValue === 'number' ? rightValue : rightValue == null ? -Infinity : Number(rightValue);

      if (normalizedLeft === normalizedRight) {
        return left.flow_name.localeCompare(right.flow_name);
      }

      return (normalizedLeft - normalizedRight) * directionMultiplier;
    });
  }, [activeSort, rows]);

  const handleSort = (field: FlowSortField, defaultDirection: FlowSortDirection) => {
    setActiveSort((current) => ({
      field,
      direction:
        current.field === field
          ? (current.direction === 'asc' ? 'desc' : 'asc')
          : defaultDirection,
    }));
  };

  const renderSortHeader = (
    field: FlowSortField,
    label: string,
    defaultDirection: FlowSortDirection,
    align: 'left' | 'right' = 'left',
  ) => {
    const isActive = activeSort.field === field;
    const isDescending = activeSort.direction === 'desc';
    const tooltipKey = FLOW_HEADER_TOOLTIPS[field];
    return (
      <div className={`wfaa-th-content${align === 'right' ? ' is-right' : ''}`}>
        <button
          type="button"
          className={`wfaa-th-sort${isActive ? ' is-active' : ''}${align === 'right' ? ' is-right' : ''}`}
          onClick={() => handleSort(field, defaultDirection)}
        >
          <span>{label}</span>
          {isActive ? (
            isDescending ? <ArrowDown className="wfaa-th-sort-icon" aria-hidden="true" /> : <ArrowUp className="wfaa-th-sort-icon" aria-hidden="true" />
          ) : null}
        </button>
        {tooltipKey ? <AnalyticsInfoTooltip tooltipKey={tooltipKey} label={label} /> : null}
      </div>
    );
  };

  return (
    <ShellCard title="Module performance" flush>
      <div className="wfaa-table-wrap wfaa-modules-table-wrap" data-testid="admin-analytics-modules-table-wrap">
        <table className="wfaa-table">
          <thead>
            <tr>
              {FLOW_SORT_COLUMNS.map((column) => (
                <th key={column.field} className={column.align === 'right' ? 'wfaa-num' : undefined}>
                  {renderSortHeader(column.field, column.label, column.defaultDirection, column.align)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.flow_name}>
                <td className="wfaa-strong">{row.flow_name}</td>
                <td className="wfaa-num">{formatNumber(row.started_runs)}</td>
                <td className="wfaa-num">{formatNumber(row.completed_runs)}</td>
                <td className="wfaa-num">{formatPercent(row.run_completion_rate)}</td>
                <td className="wfaa-num">{formatNumber(row.voice_only_completed_runs)}</td>
                <td className="wfaa-num">{formatNumber(row.standard_only_completed_runs)}</td>
                <td className="wfaa-num">{formatNumber(row.mixed_mode_completed_runs)}</td>
                <td className="wfaa-num">{formatNumber(row.started_users)}</td>
                <td className="wfaa-num">{formatNumber(row.completed_users)}</td>
                <td className="wfaa-num">{formatPercent(row.user_completion_rate)}</td>
                <td className="wfaa-num">
                  {row.median_completion_minutes == null ? '-' : row.median_completion_minutes.toFixed(1)}
                </td>
                <td className="wfaa-num">{formatNumber(row.entry_events)}</td>
                <td className="wfaa-num">{row.avg_entries_per_run.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ShellCard>
  );
};

const FlowQuestionModeSummaryCard: React.FC<{
  summary: AdminAnalyticsDashboardData['flows']['question_mode_summary'] | undefined;
}> = ({ summary }) => {
  const standardAnswers = summary?.standard_answers ?? 0;
  const voiceAnswers = summary?.voice_answers ?? 0;
  const totalAnswers = summary?.total_answers ?? 0;
  const voiceShare = summary?.voice_answer_share ?? 0;

  return (
    <ShellCard
      title={<AnalyticsLabelWithInfo label="Answers by mode" tooltipKey="flows_answers_by_mode" />}
      eyebrow="Question-level"
    >
      <p className="wfaa-note-copy wfaa-flow-mode-note">
        Counts reflect the latest canonical completed response per question within modules started in the selected period.
      </p>
      <div className="wfaa-flow-mode-summary">
        <div className="wfaa-flow-mode-stat">
          <span className="wfaa-flow-mode-label">Standard answers</span>
          <span className="wfaa-flow-mode-value">{formatNumber(standardAnswers)}</span>
        </div>
        <div className="wfaa-flow-mode-stat">
          <span className="wfaa-flow-mode-label">Voice answers</span>
          <span className="wfaa-flow-mode-value">{formatNumber(voiceAnswers)}</span>
        </div>
        <div className="wfaa-flow-mode-stat">
          <span className="wfaa-flow-mode-label">Total answers</span>
          <span className="wfaa-flow-mode-value">{formatNumber(totalAnswers)}</span>
        </div>
        <div className="wfaa-flow-mode-stat">
          <span className="wfaa-flow-mode-label">Voice answer share</span>
          <span className="wfaa-flow-mode-value">{formatPercent(voiceShare)}</span>
        </div>
      </div>
      <div className="wfaa-flow-mode-track" aria-hidden="true">
        <div
          className="wfaa-flow-mode-track-standard"
          style={{ width: `${totalAnswers > 0 ? Math.round((standardAnswers / totalAnswers) * 100) : 0}%` }}
        />
        <div
          className="wfaa-flow-mode-track-voice"
          style={{ width: `${totalAnswers > 0 ? Math.round((voiceAnswers / totalAnswers) * 100) : 0}%` }}
        />
      </div>
    </ShellCard>
  );
};

const QualityTrackedCounts: React.FC<{ rows: AdminAnalyticsTrackedCountRow[] }> = ({ rows }) => (
  <ShellCard title="Tracked quality event counts">
    <div className="wfaa-quality-counts">
      {rows.map((row) => (
        <div key={row.event_type} className="wfaa-quality-count">
          <span className="wfaa-strong">{row.event_type}</span>
          <span className="wfaa-muted">{formatNumber(row.count)}</span>
        </div>
      ))}
    </div>
  </ShellCard>
);

const AdminAnalytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedUrlState = useMemo(() => parseAdminAnalyticsSearchParams(searchParams), [searchParams]);
  const { addToast } = useToast();
  const { track } = useEventTracking();
  const [activeTab, setActiveTab] = useState<TabValue>(parsedUrlState.tab);
  const isPerformanceTab = activeTab === 'performance';
  const [openDropdown, setOpenDropdown] = useState<DropdownValue>(null);
  const [filtersPayload, setFiltersPayload] = useState<AdminAnalyticsFiltersResponse | null>(null);
  const [dashboard, setDashboard] = useState<AdminAnalyticsDashboardData | null>(null);
  const [usersPayload, setUsersPayload] = useState<AdminAnalyticsUsersResponse | null>(null);
  const [usersInitialized, setUsersInitialized] = useState(parsedUrlState.tab === 'users');
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [dashboardExporting, setDashboardExporting] = useState(false);
  const [usersExporting, setUsersExporting] = useState(false);

  const [preset, setPreset] = useState<PresetValue>(parsedUrlState.preset);
  const [dateRange, setDateRange] = useState(parsedUrlState.dateRange);
  const [organizationIds, setOrganizationIds] = useState<string[]>(parsedUrlState.organizationIds);
  const [cohortIds, setCohortIds] = useState<string[]>(parsedUrlState.cohortIds);
  const [flowIds, setFlowIds] = useState<string[]>(parsedUrlState.flowIds);
  const [search, setSearch] = useState(parsedUrlState.userSearch);
  const [committedSearch, setCommittedSearch] = useState(parsedUrlState.userSearch);
  const [includeStaff, setIncludeStaff] = useState(parsedUrlState.includeStaff);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
  const activeUserSort = useMemo(() => parseUserSort(sort), [sort]);
  const urlSyncedRef = useRef(false);
  const lastSyncedSearchRef = useRef(searchParams.toString());
  const lastAppliedFilterKeyRef = useRef(parsedUrlState.filterStateKey);
  const usersTableWrapRef = useRef<HTMLDivElement | null>(null);

  const dashboardQueryParams = useMemo(
    () => ({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      organization_ids: organizationIds,
      cohort_ids: cohortIds,
      ...(flowIds.length ? { flow_ids: flowIds } : {}),
      include_staff: includeStaff,
    }),
    [cohortIds, dateRange.endDate, dateRange.startDate, flowIds, includeStaff, organizationIds],
  );

  const usersQueryParams = useMemo(
    () => ({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      organization_ids: organizationIds,
      cohort_ids: cohortIds,
      include_staff: includeStaff,
      search: committedSearch,
      page,
      page_size: 25,
      sort,
    }),
    [cohortIds, committedSearch, dateRange.endDate, dateRange.startDate, includeStaff, organizationIds, page, sort],
  );

  const overviewRateData = useMemo(
    () =>
      (dashboard?.overview.daily_trends ?? []).map((row) => ({
        ...row,
        completion_rate: row.started_runs > 0 ? row.completed_runs / row.started_runs : 0,
      })),
    [dashboard?.overview.daily_trends],
  );

  const userRows = usersPayload?.results ?? [];
  const coverageNote = dashboard?.meta.coverage_note ?? usersPayload?.meta.coverage_note ?? '';
  const shouldFetchUsers = !isPerformanceTab && (usersInitialized || activeTab === 'users');
  const urlState = useMemo(
    () => ({
      tab: activeTab,
      preset,
      dateRange,
      organizationIds,
      cohortIds,
      flowIds,
      includeStaff,
      userSearch: committedSearch,
    }),
    [activeTab, cohortIds, committedSearch, dateRange, flowIds, includeStaff, organizationIds, preset],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextSearch = search.trim();
      setCommittedSearch((current) => {
        if (current === nextSearch) {
          return current;
        }
        setPage(1);
        setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
        return nextSearch;
      });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  useEffect(() => {
    if (activeTab === 'users' && !usersInitialized) {
      setUsersInitialized(true);
    }
  }, [activeTab, usersInitialized]);

  useEffect(() => {
    const filterStateChanged = parsedUrlState.filterStateKey !== lastAppliedFilterKeyRef.current;

    setActiveTab((current) => (current === parsedUrlState.tab ? current : parsedUrlState.tab));
    setPreset((current) => (current === parsedUrlState.preset ? current : parsedUrlState.preset));
    setDateRange((current) =>
      current.startDate === parsedUrlState.dateRange.startDate && current.endDate === parsedUrlState.dateRange.endDate
        ? current
        : parsedUrlState.dateRange
    );
    setOrganizationIds((current) =>
      current.join('|') === parsedUrlState.organizationIds.join('|') ? current : parsedUrlState.organizationIds
    );
    setCohortIds((current) =>
      current.join('|') === parsedUrlState.cohortIds.join('|') ? current : parsedUrlState.cohortIds
    );
    setFlowIds((current) => (current.join('|') === parsedUrlState.flowIds.join('|') ? current : parsedUrlState.flowIds));
    setIncludeStaff((current) => (current === parsedUrlState.includeStaff ? current : parsedUrlState.includeStaff));
    setSearch((current) => (current === parsedUrlState.userSearch ? current : parsedUrlState.userSearch));
    setCommittedSearch((current) => (current === parsedUrlState.userSearch ? current : parsedUrlState.userSearch));

    if (parsedUrlState.tab === 'users') {
      setUsersInitialized(true);
    }

    if (filterStateChanged) {
      setPage(1);
      setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
    }

    lastAppliedFilterKeyRef.current = parsedUrlState.filterStateKey;
  }, [parsedUrlState]);

  useEffect(() => {
    const nextSearchParams = buildAdminAnalyticsSearchParams(searchParams, urlState);
    const nextQueryString = nextSearchParams.toString();
    const currentQueryString = searchParams.toString();

    // Let URL-driven state restoration finish before writing local state back.
    // Otherwise back/forward navigation can be overwritten by the previous tab.
    if (currentQueryString !== lastSyncedSearchRef.current) {
      lastSyncedSearchRef.current = currentQueryString;
      return;
    }

    if (nextQueryString === currentQueryString) {
      urlSyncedRef.current = true;
      return;
    }

    setSearchParams(nextSearchParams, { replace: !urlSyncedRef.current });
    urlSyncedRef.current = true;
  }, [searchParams, setSearchParams, urlState]);

  useEffect(() => {
    if (isPerformanceTab) {
      return undefined;
    }
    let isActive = true;
    setIsLoading(true);

    adminAnalytics
      .getFilters()
      .then((payload) => {
        if (!isActive) {
          return;
        }
        setFiltersPayload(payload);
      })
      .catch((requestError) => {
        console.error('Failed to load admin analytics filters', requestError);
        if (!isActive) {
          return;
        }
        setError('Unable to load analytics filters right now.');
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isPerformanceTab]);

  useEffect(() => {
    if (isPerformanceTab) {
      return undefined;
    }
    let isActive = true;
    setError(null);
    setDashboardLoading(true);

    adminAnalytics
      .getDashboard(dashboardQueryParams)
      .then((dashboardResponse) => {
        if (!isActive) {
          return;
        }
        setDashboard(dashboardResponse);
      })
      .catch((requestError) => {
        console.error('Failed to load admin analytics data', requestError);
        if (!isActive) {
          return;
        }
        setError('Unable to load analytics data for the selected filters.');
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setDashboardLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [dashboardQueryParams, isPerformanceTab]);

  useEffect(() => {
    if (!shouldFetchUsers) {
      return undefined;
    }

    let isActive = true;
    setUsersLoading(true);
    setUsersError(null);

    adminAnalytics
      .getUsers(usersQueryParams)
      .then((usersResponse) => {
        if (!isActive) {
          return;
        }
        setUsersPayload(usersResponse);
      })
      .catch((requestError) => {
        console.error('Failed to load admin analytics users', requestError);
        if (!isActive) {
          return;
        }
        setUsersError('Unable to load user analytics for the selected filters.');
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setUsersLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [shouldFetchUsers, usersQueryParams]);

  const handlePresetChange = (nextPreset: PresetValue) => {
    setPreset(nextPreset);
    if (nextPreset !== 'custom') {
      setDateRange(getAdminAnalyticsPresetRange(nextPreset));
    }
    setPage(1);
    setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
  };

  const handleUserSort = (field: UserSortField, defaultDirection: UserSortDirection) => {
    const nextDirection =
      activeUserSort.field === field
        ? (activeUserSort.direction === 'asc' ? 'desc' : 'asc')
        : defaultDirection;
    setSort(`${field}_${nextDirection}`);
    setPage(1);
  };

  const handleDashboardExport = async () => {
    try {
      setDashboardExporting(true);
      track('button_click', {
        action: 'admin_analytics_export_dashboard',
        control_id: 'admin-analytics-export-dashboard',
        screen_name: '/admin/analytics',
        surface: 'admin_analytics',
      });
      const response = await adminAnalytics.exportDashboard(dashboardQueryParams);
      downloadBlobResponse(response, 'admin-analytics-dashboard.csv');
    } catch (requestError) {
      console.error('Failed to export dashboard analytics', requestError);
      addToast({ title: 'Export failed', description: 'Unable to export dashboard data.', variant: 'destructive' });
    } finally {
      setDashboardExporting(false);
    }
  };

  const handleUsersExport = async () => {
    try {
      setUsersExporting(true);
      track('button_click', {
        action: 'admin_analytics_export_users',
        control_id: 'admin-analytics-export-users',
        screen_name: '/admin/analytics',
        surface: 'admin_analytics',
      });
      const response = await adminAnalytics.exportUsers(usersQueryParams);
      downloadBlobResponse(response, 'admin-analytics-users.csv');
    } catch (requestError) {
      console.error('Failed to export user analytics', requestError);
      addToast({ title: 'Export failed', description: 'Unable to export users data.', variant: 'destructive' });
    } finally {
      setUsersExporting(false);
    }
  };

  const renderUserSortHeader = (
    field: UserSortField,
    label: string,
    defaultDirection: UserSortDirection,
    align: 'left' | 'right' = 'left',
  ) => {
    const isActive = activeUserSort.field === field;
    const isDescending = activeUserSort.direction === 'desc';
    const tooltipKey = USER_HEADER_TOOLTIPS[field];
    return (
      <div className={`wfaa-th-content${align === 'right' ? ' is-right' : ''}`}>
        <button
          type="button"
          className={`wfaa-th-sort${isActive ? ' is-active' : ''}${align === 'right' ? ' is-right' : ''}`}
          onClick={() => handleUserSort(field, defaultDirection)}
        >
          <span>{label}</span>
          {isActive ? (
            isDescending ? <ArrowDown className="wfaa-th-sort-icon" aria-hidden="true" /> : <ArrowUp className="wfaa-th-sort-icon" aria-hidden="true" />
          ) : null}
        </button>
        {tooltipKey ? <AnalyticsInfoTooltip tooltipKey={tooltipKey} label={label} /> : null}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <section className="wf-admin-analytics" data-testid="admin-analytics-page">
      <div className="wfaa-page-title">
        <div className="wfaa-page-title-text">
          <div className="wfaa-page-eyebrow">Internal · Staff only</div>
          <h1 className="wfaa-page-h1">Analytics</h1>
          <p className="wfaa-page-copy">
            Internal analytics across usage, device patterns, screen engagement, module performance,
            user-level activity, and app load times.
          </p>
        </div>
        {!isPerformanceTab && <div className="wfaa-page-actions">
          <button
            type="button"
            className="wfaa-btn-primary"
            onClick={handleDashboardExport}
            disabled={dashboardExporting}
            data-testid="admin-analytics-export-dashboard"
          >
            <Download />
            Export dashboard CSV
          </button>
        </div>}
      </div>

      <div className="wfaa-tabs" role="tablist" aria-label="Analytics sections">
        {TAB_ORDER.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`admin-analytics-tab-${tab.id}`}
            aria-controls={`admin-analytics-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={`wfaa-tab${activeTab === tab.id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isPerformanceTab ? (
        <div className="wfaa-main">
          <section
            role="tabpanel"
            id="admin-analytics-panel-performance"
            aria-labelledby="admin-analytics-tab-performance"
            className="wfaa-section"
          >
            <PerformanceMetricsCard />
          </section>
        </div>
      ) : (isLoading && !filtersPayload) || (dashboardLoading && !dashboard) ? (
        <div className="flex min-h-64 items-center justify-center" role="status" aria-label="Loading analytics">
          <LoadingAnimation size="lg" />
        </div>
      ) : <>
      <div className="wfaa-filterbar">
        <div className="wfaa-filter-section">
          <span className="wfaa-filter-section-label">Range</span>
          <div className="wfaa-seg">
            {(['30d', '90d', 'custom'] as PresetValue[]).map((option) => (
              <button
                key={option}
                type="button"
                className={`wfaa-seg-btn${preset === option ? ' is-active' : ''}`}
                onClick={() => handlePresetChange(option)}
              >
                {option === '30d' ? '30d' : option === '90d' ? '90d' : 'Custom'}
              </button>
            ))}
          </div>
          {preset === 'custom' ? (
            <div className="wfaa-custom-range">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(event) => {
                  setDateRange((current) => ({ ...current, startDate: event.currentTarget.value }));
                  setPage(1);
                  setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
                }}
              />
              <span className="wfaa-custom-range-dash">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(event) => {
                  setDateRange((current) => ({ ...current, endDate: event.currentTarget.value }));
                  setPage(1);
                  setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="wfaa-divider" />

        <div className="wfaa-filter-section">
          <FilterDropdown
            label="Org"
            options={filtersPayload?.organizations ?? []}
            value={organizationIds}
            open={openDropdown === 'organizations'}
            onOpenChange={(open) => setOpenDropdown(open ? 'organizations' : null)}
            onChange={(nextValue) => {
              setOrganizationIds(nextValue);
              setPage(1);
              setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
            }}
            testId="admin-analytics-org-filter"
          />
          <FilterDropdown
            label="Cohort"
            options={filtersPayload?.cohorts ?? []}
            value={cohortIds}
            open={openDropdown === 'cohorts'}
            onOpenChange={(open) => setOpenDropdown(open ? 'cohorts' : null)}
            onChange={(nextValue) => {
              setCohortIds(nextValue);
              setPage(1);
              setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
            }}
            testId="admin-analytics-cohort-filter"
          />
          {activeTab === 'flows' ? (
            <FilterDropdown
              label="Module"
              options={filtersPayload?.flows ?? []}
              value={flowIds}
              open={openDropdown === 'flows'}
              onOpenChange={(open) => setOpenDropdown(open ? 'flows' : null)}
              onChange={(nextValue) => {
                setFlowIds(nextValue);
              }}
              searchable
              searchPlaceholder="Search modules"
              testId="admin-analytics-flow-filter"
            />
          ) : null}
        </div>

        {activeTab === 'users' ? (
          <div className="wfaa-search">
            <Search className="h-4 w-4" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
              }}
              placeholder="Search users"
              aria-label="Search users"
            />
          </div>
        ) : null}

        <label className="wfaa-toggle">
          <input
            type="checkbox"
            checked={includeStaff}
            onChange={(event) => {
              setIncludeStaff(event.currentTarget.checked);
              setPage(1);
              setSort(ADMIN_ANALYTICS_DEFAULT_USER_SORT);
            }}
          />
          <span className="wfaa-toggle-track">
            <span className="wfaa-toggle-thumb" />
          </span>
          <span>Include staff</span>
        </label>

        <div className="wfaa-spacer" />

        {coverageNote ? <div className="wfaa-filter-note">{coverageNote}</div> : null}
      </div>

      <div className="wfaa-main">
        {error ? <div className="wfaa-error">{error}</div> : null}

        {activeTab === 'overview' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-overview"
            aria-labelledby="admin-analytics-tab-overview"
            className="wfaa-section"
          >
            <SectionHeader
              title="Overview"
              description="High-level usage and quality key performance indicators across the selected population and date range."
            />

            <div className="wfaa-kpi-grid">
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Active users" tooltipKey="overview_active_users" />}
                value={formatNumber(dashboard?.overview.active_users ?? 0)}
                accent
              />
              <MetricCard label="New users" value={formatNumber(dashboard?.overview.new_users ?? 0)} />
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Returning users" tooltipKey="overview_returning_users" />}
                value={formatNumber(dashboard?.overview.returning_users ?? 0)}
              />
              <MetricCard label="Page views" value={formatNumber(dashboard?.overview.page_views ?? 0)} />
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Started modules" tooltipKey="overview_started_modules" />}
                value={formatNumber(dashboard?.overview.started_runs ?? 0)}
              />
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Completed modules" tooltipKey="overview_completed_modules" />}
                value={formatNumber(dashboard?.overview.completed_runs ?? 0)}
              />
              <MetricCard
                label={
                  <AnalyticsLabelWithInfo
                    label="Module completion rate"
                    tooltipKey="overview_module_completion_rate"
                  />
                }
                value={formatPercent(dashboard?.overview.run_completion_rate ?? 0)}
              />
              <MetricCard
                label="Active organizations"
                value={formatNumber(dashboard?.overview.active_organizations ?? 0)}
              />
              <MetricCard label="Active cohorts" value={formatNumber(dashboard?.overview.active_cohorts ?? 0)} />
              <MetricCard label="Bug reports created" value={formatNumber(dashboard?.overview.bug_reports_created ?? 0)} />
            </div>

            <ShellCard eyebrow="Completion mode" title="How completed modules were finished">
              <div className="wfaa-kpi-grid wfaa-kpi-grid-compact">
                <MetricCard
                  label={<AnalyticsLabelWithInfo label="Voice-only completions" tooltipKey="overview_voice_only_completions" />}
                  value={formatNumber(dashboard?.overview.voice_only_completed_runs ?? 0)}
                />
                <MetricCard
                  label={<AnalyticsLabelWithInfo label="Standard-only completions" tooltipKey="overview_standard_only_completions" />}
                  value={formatNumber(dashboard?.overview.standard_only_completed_runs ?? 0)}
                />
                <MetricCard
                  label={<AnalyticsLabelWithInfo label="Mixed-mode completions" tooltipKey="overview_mixed_mode_completions" />}
                  value={formatNumber(dashboard?.overview.mixed_mode_completed_runs ?? 0)}
                />
              </div>
            </ShellCard>

            <div className="wfaa-grid-2">
              <ShellCard eyebrow="Engagement" title="Daily active users + completed modules">
                <div className="wfaa-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboard?.overview.daily_trends ?? []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d7dae4" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#787a84' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#787a84' }} />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="active_users" stroke="#353947" strokeWidth={2.4} dot={false} />
                      <Line
                        type="monotone"
                        dataKey="completed_runs"
                        stroke="#fd4319"
                        strokeWidth={2.4}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="wfaa-chart-legend">
                  <span className="wfaa-legend-inline">
                    <span className="wfaa-legend-dot" style={{ backgroundColor: '#353947' }} />
                    Active users
                  </span>
                  <span className="wfaa-legend-inline">
                    <span className="wfaa-legend-dot" style={{ backgroundColor: '#fd4319' }} />
                    Completed modules
                  </span>
                </div>
              </ShellCard>

              <ShellCard eyebrow="Quality" title="Module completion rate over time">
                <div className="wfaa-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={overviewRateData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d7dae4" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#787a84' }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#787a84' }}
                        tickFormatter={(value: number) => `${Math.round(value * 100)}%`}
                      />
                      <RechartsTooltip formatter={(value: number) => formatPercent(value)} />
                      <Line type="monotone" dataKey="completion_rate" stroke="#353947" strokeWidth={2.4} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="wfaa-card-foot">
                  <span className="wfaa-card-metric">
                    {formatPercent(dashboard?.overview.run_completion_rate ?? 0)}
                  </span>
                  <span className="wfaa-muted">overall selected range</span>
                </div>
              </ShellCard>
            </div>

            <ShellCard eyebrow="Historical attribution" title="Note on cohort and org snapshots">
              <p className="wfaa-note-copy">
                {coverageNote ||
                  'Events recorded after the snapshot rollout carry org and cohort snapshot fields. Older events can fall back to the user current organization and cohort, so historical attribution before rollout may be less precise.'}
              </p>
            </ShellCard>
          </section>
        ) : null}

        {activeTab === 'devices' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-devices"
            aria-labelledby="admin-analytics-tab-devices"
            className="wfaa-section"
          >
            <SectionHeader
              title="Devices"
              description="How the selected population is accessing the app across device class, browser, and operating system."
            />

            <div className="wfaa-grid-3">
              <DeviceClassChartCard
                title={<AnalyticsLabelWithInfo label="Device class" tooltipKey="devices_device_class" />}
                rows={dashboard?.devices.device_class_distribution ?? []}
              />
              <HorizontalDistributionCard title="Browser" rows={dashboard?.devices.browser_distribution ?? []} />
              <HorizontalDistributionCard title="Operating system" rows={dashboard?.devices.os_distribution ?? []} />
            </div>

            <DeviceGroupTable
              title="Device usage by organization"
              rows={dashboard?.devices.by_organization ?? []}
              labelKey="organization_name"
            />

            <DeviceGroupTable
              title="Device usage by cohort"
              rows={dashboard?.devices.by_cohort ?? []}
              labelKey="cohort_name"
            />

            {usersInitialized ? (
              <UserSampleTable rows={userRows.slice(0, 10)} />
            ) : (
              <ShellCard eyebrow="Sample" title="Per-user device usage">
                <p className="wfaa-note-copy">
                  Open the Users tab to load the current per-user device sample for this filter set.
                </p>
              </ShellCard>
            )}
          </section>
        ) : null}

        {activeTab === 'screens' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-screens"
            aria-labelledby="admin-analytics-tab-screens"
            className="wfaa-section"
          >
            <SectionHeader
              title="Screens"
              description="Routes, page views, and downstream actions."
            />

            <TopRoutesTable rows={dashboard?.screens.routes ?? []} />

            <TopButtonActionsCard rows={(dashboard?.screens.top_actions ?? []).slice(0, 8)} />

            <LowConversionTable rows={dashboard?.screens.low_conversion_routes ?? []} />
          </section>
        ) : null}

        {activeTab === 'flows' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-flows"
            aria-labelledby="admin-analytics-tab-flows"
            className="wfaa-section"
          >
            <SectionHeader
              title="Modules"
              description="Module-based completion metrics for modules started in the selected period. Select specific modules to replace the default top-list view."
            />

            <ShellCard title="Metric note">
              <p className="wfaa-note-copy">
                Completion metrics are based on modules started in the selected date range.
              </p>
              <p className="wfaa-note-copy">
                {flowIds.length
                  ? 'Showing only the selected modules.'
                  : 'No modules selected, so this tab shows the top modules automatically.'}
              </p>
            </ShellCard>

            <FlowPerformanceTable rows={dashboard?.flows.rows ?? []} />

            <div className="wfaa-grid-2">
              <FlowQuestionModeSummaryCard summary={dashboard?.flows.question_mode_summary} />
            </div>
          </section>
        ) : null}

        {activeTab === 'users' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-users"
            aria-labelledby="admin-analytics-tab-users"
            className="wfaa-section"
          >
            <SectionHeader
              title="Users"
              description="User-level analytics for device usage, activity, and module performance across the active filter set."
              actions={(
                <div className="wfaa-users-actions">
                  <button
                    type="button"
                    className="wfaa-btn-primary"
                    onClick={handleUsersExport}
                    disabled={usersExporting}
                    data-testid="admin-analytics-export-users"
                  >
                    <Download />
                    Export users CSV
                  </button>
                </div>
              )}
            />

            <ShellCard flush>
              <div
                className={`wfaa-users-table-shell${usersLoading ? ' is-loading' : ''}`}
                aria-busy={usersLoading}
                data-testid="admin-analytics-users-table-shell"
              >
                {usersLoading ? (
                  <div className="wfaa-users-table-overlay" data-testid="admin-analytics-users-loading">
                    Updating users...
                  </div>
                ) : null}
                {usersError ? <div className="wfaa-users-error">{usersError}</div> : null}
                <div className="wfaa-table-wrap wfaa-users-table-wrap" ref={usersTableWrapRef}>
                  <table className="wfaa-table">
                  <thead>
                    <tr>
                      {USER_SORT_COLUMNS.map((column) => (
                        <th key={column.field} className={column.align === 'right' ? 'wfaa-num' : undefined}>
                          {renderUserSortHeader(column.field, column.label, column.defaultDirection, column.align)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userRows.map((user) => (
                      <tr key={user.user_id}>
                        <td>
                          <div className="wfaa-user-cell">
                            <span className="wfaa-strong">{user.display_name}</span>
                            <span className="wfaa-muted wfaa-user-email">{user.email}</span>
                          </div>
                        </td>
                        <td>{user.organization_name ?? '-'}</td>
                        <td>{user.current_cohort_name ?? '-'}</td>
                        <td>
                          <span className="wfaa-chip">{formatTitleCase(user.primary_device_class) || '-'}</span>
                        </td>
                        <td>{user.browsers_seen.join(', ') || '-'}</td>
                        <td>{user.oses_seen.join(', ') || '-'}</td>
                        <td className="wfaa-num">{formatNumber(user.page_views)}</td>
                        <td className="wfaa-num">{formatNumber(user.started_runs)}</td>
                        <td className="wfaa-num">{formatNumber(user.completed_runs)}</td>
                        <td className="wfaa-num">{formatPercent(user.completion_rate)}</td>
                        <td>{user.last_route_seen ?? '-'}</td>
                        <td>{formatDateTime(user.last_active_at)}</td>
                      </tr>
                    ))}
                    {!userRows.length && usersLoading ? (
                      Array.from({ length: 6 }).map((_, index) => (
                        <tr key={`users-loading-${index}`} className="wfaa-skeleton-row" aria-hidden="true">
                          <td colSpan={12}>
                            <div className="wfaa-skeleton-line" />
                          </td>
                        </tr>
                      ))
                    ) : null}
                    {!userRows.length && !usersLoading && !usersError ? (
                      <tr>
                        <td colSpan={12} className="wfaa-empty">
                          No users matched the selected filters.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                  </table>
                </div>
              </div>
            </ShellCard>

            <PaginationControls
              page={usersPayload?.page ?? page}
              pageSize={usersPayload?.page_size ?? 25}
              totalCount={usersPayload?.total_count ?? 0}
              onPageChange={setPage}
              className="wfaa-pagination"
            />
          </section>
        ) : null}

        {activeTab === 'quality' ? (
          <section
            role="tabpanel"
            id="admin-analytics-panel-quality"
            aria-labelledby="admin-analytics-tab-quality"
            className="wfaa-section"
          >
            <SectionHeader
              title="Quality"
              description="Tracked auth, bug reporting, support-seeking CTAs, toolkit activity, and other internal quality signals."
            />

            <div className="wfaa-kpi-grid wfaa-kpi-grid-quality">
              <MetricCard label="Logins" value={formatNumber(dashboard?.quality.login_count ?? 0)} accent />
              <MetricCard label="Logouts" value={formatNumber(dashboard?.quality.logout_count ?? 0)} />
              <MetricCard label="Password changes" value={formatNumber(dashboard?.quality.password_change_count ?? 0)} />
              <MetricCard label="Bug reports" value={formatNumber(dashboard?.quality.bug_reports_created ?? 0)} />
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Support CTA clicks" tooltipKey="quality_support_cta_clicks" />}
                value={formatNumber(dashboard?.quality.support_cta_click_count ?? 0)}
              />
              <MetricCard
                label={<AnalyticsLabelWithInfo label="Toolkit note activity" tooltipKey="quality_toolkit_note_activity" />}
                value={formatNumber(dashboard?.quality.toolkit_note_activity_count ?? 0)}
              />
            </div>

            <QualityTrackedCounts rows={dashboard?.quality.tracked_counts ?? []} />

            <ShellCard title="Coverage note">
              <p className="wfaa-note-copy">
                {dashboard?.quality.coverage_note ?? 'Coverage details are unavailable.'}
              </p>
            </ShellCard>
          </section>
        ) : null}
      </div>
      </>}
      </section>
    </TooltipProvider>
  );
};

export default AdminAnalytics;
